import { io } from 'socket.io-client'

const SOCKET_URL = process.env.VUE_APP_API_URL || 'https://brs-api.tubtimcrm.com'

/**
 * Singleton Socket.IO client — one browser tab, one connection.
 * Reuse the same instance on disconnect (built-in reconnection / connect());
 * never create a second io() while an instance still exists.
 * @see skills/socket-io-reuse-instance-on-tab-resume
 */
class SocketioService {
  socket = null
  boundAgentEvent = null

  setupSocketConnection () {
    if (this.socket) {
      if (this.socket.disconnected) {
        this.socket.connect()
      }
      return this.socket
    }

    this.socket = io(SOCKET_URL, {
      // Match API: WebSocket only (no long-polling) — see create-socket-server.js
      transports: ['websocket'],
      upgrade: false,
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: Infinity
    })
    return this.socket
  }

  /**
   * Tab became visible — reconnect the same instance (do not create a new io()).
   */
  resumeIfNeeded () {
    if (!this.socket) return null
    if (this.socket.disconnected) {
      this.socket.connect()
    }
    return this.socket
  }

  /**
   * Ensure socket exists and agent channel listeners are bound once per agent id.
   * Relies on Socket.IO reconnection — do not call setupSocketConnection from disconnect.
   */
  ensureAgentChannel (profileId, { onConnect, onDisconnect, onAgentEvent } = {}) {
    if (profileId == null || profileId === '') {
      return null
    }

    this.setupSocketConnection()
    const agentEvent = `agent-${profileId}`

    if (this.boundAgentEvent === agentEvent) {
      return this.socket
    }

    if (this.boundAgentEvent && this.socket) {
      this.socket.off(this.boundAgentEvent)
      this.socket.off('connect')
      this.socket.off('disconnect')
    }

    if (onConnect) this.socket.on('connect', onConnect)
    if (onDisconnect) {
      this.socket.on('disconnect', (reason) => {
        onDisconnect(reason)
      })
    }
    if (onAgentEvent) this.socket.on(agentEvent, onAgentEvent)
    this.boundAgentEvent = agentEvent
    return this.socket
  }

  disconnect () {
    if (!this.socket) return
    this.socket.removeAllListeners()
    this.socket.disconnect()
    this.socket = null
    this.boundAgentEvent = null
  }
}

export default new SocketioService()
